import { useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import { useContext } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { usePersistedQuery } from 'hooks/usePersistedQuery';
import { useSearchInput } from 'hooks/useSearchInput';
import { AppContext } from 'shared/AppContext';
import { useEventYearFilter } from '../Components/EventYearFilter';
import {
  CommonEventFieldsFragment,
  EditTicketFieldsFragment,
  TicketFieldsFragment,
} from './Events.fragments';

export const GET_EVENT = gql`
  query GetEvent($input: GetEventInput!) {
    getEvent(input: $input) {
      ...editTicketFields
    }
  }
  ${EditTicketFieldsFragment}
`;

export function useGetEvent(currentEventId, skip) {
  const { id: idParam } = useParams();
  const { pathname } = useLocation();
  const isTrackEventsView = pathname.includes('/admin/track/events');
  const id =
    currentEventId ?? (!isTrackEventsView && idParam ? Number(idParam) : null);
  return useQuery(GET_EVENT, {
    variables: { input: { id } },
    skip: !id || skip,
    fetchPolicy: 'no-cache',
  });
}

export const GET_EVENT_HEADER_INFO = gql`
  query GetEventHeaderInfo($input: GetEventInput!) {
    getEvent(input: $input) {
      ...commonEventFields
      next_date
      tickets {
        name
        price
        date
      }
      other_tickets {
        name
        price
      }
      isMultiDay
      listDates
      day
      date
      month
    }
  }
  ${CommonEventFieldsFragment}
`;

export function useGetEventHeaderInfo() {
  const { id } = useParams();
  return useQuery(GET_EVENT_HEADER_INFO, {
    variables: { input: { id: Number(id) } },
    skip: !id,
  });
}

export const GET_EVENTS_ADMIN = gql`
  query GetEventsAdmin($input: GetEventsAdminInput!) {
    getEventsAdmin(input: $input) {
      count
      results {
        ...commonEventFields
        tickets {
          ...ticketFields
        }
        admin_tickets {
          ...ticketFields
        }
        admin_multiday_tickets {
          ...ticketFields
        }
        admin_other_tickets {
          ...ticketFields
        }
        admin_bundle_tickets {
          ...ticketFields
        }
      }
    }
  }
  ${CommonEventFieldsFragment}
  ${TicketFieldsFragment}
`;

/**
 *
 * @param {string} type - 'track' | 'series' | 'events'
 */
export function useGetEventsAdmin(type, options = {}, limit) {
  const {
    yearFilter,
    trackFilter,
    seriesFilter,
    accountFilter,
    startDateFilter,
    endDateFilter,
    startTransactionDateFilter,
    endTransactionDateFilter,
  } = useEventYearFilter();
  const { input: search } = useSearchInput();
  const { id } = useParams();
  const { pathname } = useLocation();

  const variables = {
    input: {
      ...search,
      ...(type === 'track' ? { track_id: Number(id) } : null),
      ...(type === 'series' ? { series_id: Number(id) } : null),
      ...(yearFilter &&
      pathname !== '/admin/events' &&
      pathname !== '/admin/events/upcoming' &&
      !(
        (startDateFilter && endDateFilter) ||
        (startTransactionDateFilter && endTransactionDateFilter)
      )
        ? { year: yearFilter }
        : null),
      ...(trackFilter ? { track_id: trackFilter.value } : null),
      ...(seriesFilter ? { series_id: seriesFilter.value } : null),
      ...(accountFilter ? { account_id: accountFilter.value } : null),
      ...(limit ? { limit: limit } : null),
      ...((startDateFilter && endDateFilter) ||
      (startTransactionDateFilter && endTransactionDateFilter)
        ? {
            start_date:
              pathname === '/admin/transactions'
                ? startTransactionDateFilter
                : startDateFilter,
          }
        : null),
      ...(endDateFilter || endTransactionDateFilter
        ? {
            end_date:
              pathname === '/admin/transactions'
                ? endTransactionDateFilter
                : endDateFilter,
          }
        : null),
    },
  };
  return useQuery(GET_EVENTS_ADMIN, {
    fetchPolicy: 'cache-and-network',
    variables,
    ...options,
  });
}

function useEventsAdminVariables(type, limit) {
  const {
    yearFilter,
    trackFilter,
    seriesFilter,
    accountFilter,
    startDateFilter,
    endDateFilter,
    startTransactionDateFilter,
    endTransactionDateFilter,
  } = useEventYearFilter();
  const { input: search } = useSearchInput();
  const { id } = useParams();
  const { pathname } = useLocation();

  return {
    input: {
      ...search,
      ...(type === 'track' ? { track_id: Number(id) } : null),
      ...(type === 'series' ? { series_id: Number(id) } : null),
      ...(yearFilter &&
      pathname !== '/admin/events' &&
      !(
        (startDateFilter && endDateFilter) ||
        (startTransactionDateFilter && endTransactionDateFilter)
      )
        ? { year: yearFilter }
        : null),
      ...(trackFilter ? { track_id: trackFilter.value } : null),
      ...(seriesFilter ? { series_id: seriesFilter.value } : null),
      ...(accountFilter ? { account_id: accountFilter.value } : null),
      ...(limit ? { limit: limit } : null),
      ...((startDateFilter && endDateFilter) ||
      (startTransactionDateFilter && endTransactionDateFilter)
        ? {
            start_date:
              pathname === '/admin/transactions'
                ? startTransactionDateFilter
                : startDateFilter,
          }
        : null),
      ...(endDateFilter || endTransactionDateFilter
        ? {
            end_date:
              pathname === '/admin/transactions'
                ? endTransactionDateFilter
                : endDateFilter,
          }
        : null),
    },
  };
}

export const GET_DRAFT_EVENTS_ADMIN = gql`
  query GetDraftEventsAdmin($input: GetEventsAdminInput!) {
    getDraftEventsAdmin(input: $input) {
      count
      results {
        ...commonEventFields
        tickets {
          ...ticketFields
        }
        admin_tickets {
          ...ticketFields
        }
        admin_multiday_tickets {
          ...ticketFields
        }
        admin_other_tickets {
          ...ticketFields
        }
        admin_bundle_tickets {
          ...ticketFields
        }
      }
    }
  }
  ${CommonEventFieldsFragment}
  ${TicketFieldsFragment}
`;

export function useGetDraftEventsAdmin(type, options = {}, limit) {
  const variables = useEventsAdminVariables(type, limit);
  return useQuery(GET_DRAFT_EVENTS_ADMIN, {
    fetchPolicy: 'cache-and-network',
    variables,
    ...options,
  });
}

export const GET_PAST_EVENTS_ADMIN = gql`
  query GetPastEventsAdmin($input: GetEventsAdminInput!) {
    getPastEventsAdmin(input: $input) {
      count
      results {
        ...commonEventFields
        tickets {
          ...ticketFields
        }
        admin_tickets {
          ...ticketFields
        }
        admin_multiday_tickets {
          ...ticketFields
        }
        admin_other_tickets {
          ...ticketFields
        }
        admin_bundle_tickets {
          ...ticketFields
        }
      }
    }
  }
  ${CommonEventFieldsFragment}
  ${TicketFieldsFragment}
`;

export function useGetPastEventsAdmin(type, options = {}, limit) {
  const variables = useEventsAdminVariables(type, limit);
  return useQuery(GET_PAST_EVENTS_ADMIN, {
    fetchPolicy: 'cache-and-network',
    variables,
    ...options,
  });
}

export const GET_USER_EVENTS = gql`
  query GetUserEvents($input: GetAccountEventsInput!) {
    getEventsByAccount(input: $input) {
      count
      results {
        ...commonEventFields
        nextGateTime
        tickets {
          name
          price
          date
        }
        other_tickets {
          name
          price
        }
      }
    }
  }
  ${CommonEventFieldsFragment}
`;

export function useGetUserEvents(search, options = {}) {
  const { yearFilter } = useEventYearFilter();
  const { input: routeSearch } = useSearchInput();
  const {
    state: { accountId },
  } = useContext(AppContext);
  const variables = {
    input: {
      ...routeSearch,
      ...(search ? { queryString: search } : null),
      ...(yearFilter ? { year: yearFilter } : null),
      ...(accountId ? { account_id: accountId } : null),
    },
  };

  const result = usePersistedQuery(
    GET_USER_EVENTS,
    `user-events-${routeSearch}-${accountId}-${search}-${yearFilter}`,
    { variables, ...options }
  );

  return { ...result };
}

export const GET_UPCOMING_USER_EVENTS = gql`
  query GetUpcomingUserEvents($input: GetAccountEventsInput!) {
    getUpcomingEventsByAccount(input: $input) {
      count
      results {
        ...commonEventFields
        nextGateTime
        tickets {
          name
          price
          date
        }
        other_tickets {
          name
          price
        }
      }
    }
  }
  ${CommonEventFieldsFragment}
`;

export function useGetUpcomingUserEvents(search, options = {}) {
  const { yearFilter } = useEventYearFilter();
  const { input: routeSearch } = useSearchInput();
  const {
    state: { accountId },
  } = useContext(AppContext);
  const variables = {
    input: {
      ...routeSearch,
      ...(search ? { queryString: search } : null),
      ...(yearFilter ? { year: yearFilter } : null),
      ...(accountId ? { account_id: accountId } : null),
    },
  };

  const result = usePersistedQuery(
    GET_UPCOMING_USER_EVENTS,
    `upcoming-user-events-${routeSearch}-${accountId}-${search}-${yearFilter}`,
    { variables, ...options }
  );

  return { ...result };
}

export const GET_PAST_USER_EVENTS = gql`
  query GetPastUserEvents($input: GetAccountEventsInput!) {
    getPastEventsByAccount(input: $input) {
      count
      results {
        ...commonEventFields
        nextGateTime
        tickets {
          name
          price
          date
        }
        other_tickets {
          name
          price
        }
      }
    }
  }
  ${CommonEventFieldsFragment}
`;

export function useGetPastUserEvents(search, options = {}) {
  const { yearFilter } = useEventYearFilter();
  const { input: routeSearch } = useSearchInput();
  const {
    state: { accountId },
  } = useContext(AppContext);
  const variables = {
    input: {
      ...routeSearch,
      ...(search ? { queryString: search } : null),
      ...(yearFilter ? { year: yearFilter } : null),
      ...(accountId ? { account_id: accountId } : null),
    },
  };

  const result = usePersistedQuery(
    GET_PAST_USER_EVENTS,
    `past-user-events-${routeSearch}-${accountId}-${search}-${yearFilter}`,
    { variables, ...options }
  );

  return { ...result };
}

export const GET_REGISTRATION_USER_EVENTS = gql`
  query GetRegistrationUserEvents($input: GetAccountEventsInput!) {
    getRegistrationEventsByAccount(input: $input) {
      count
      results {
        ...commonEventFields
        nextGateTime
        tickets {
          name
          price
          date
        }
        other_tickets {
          name
          price
        }
      }
    }
  }
  ${CommonEventFieldsFragment}
`;

export function useGetRegistrationUserEvents(search, options = {}) {
  const { yearFilter } = useEventYearFilter();
  const { input: routeSearch } = useSearchInput();
  const {
    state: { accountId },
  } = useContext(AppContext);
  const variables = {
    input: {
      ...routeSearch,
      ...(search ? { queryString: search } : null),
      ...(yearFilter ? { year: yearFilter } : null),
      ...(accountId ? { account_id: accountId } : null),
    },
  };

  const result = usePersistedQuery(
    GET_REGISTRATION_USER_EVENTS,
    `registration-user-events-${routeSearch}-${accountId}-${search}-${yearFilter}`,
    { variables, ...options }
  );

  return { ...result };
}

export const GET_REGISTRATION_EVENTS_COUNT = gql`
  query GetRegistrationEventsCountByAccount($account_id: Int!) {
    getRegistrationEventsCountByAccount(account_id: $account_id)
  }
`;

export function useRegistrationEventsCount(options = {}) {
  const {
    state: { accountId },
  } = useContext(AppContext);
  const { skip, ...rest } = options;
  return useQuery(GET_REGISTRATION_EVENTS_COUNT, {
    variables: { account_id: accountId },
    skip: !accountId || skip,
    ...rest,
  });
}

const GET_ALL_TRACKS = gql`
  query GetAllTracks($input: GetTracksInput!) {
    getTracks(input: $input) {
      id
      name
    }
  }
`;

const GET_ALL_SERIES = gql`
  query GetAllSeries($input: GetSeriesInput!) {
    getSeries(input: $input) {
      id
      name
    }
  }
`;

export function useGetAllTracks() {
  return useQuery(GET_ALL_TRACKS, {
    variables: { input: {} },
  });
}

export function useGetAllSeries() {
  return useQuery(GET_ALL_SERIES, {
    variables: { input: {} },
  });
}
